import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  FileText, CheckCircle, XCircle, Clock, ExternalLink,
  Filter, RefreshCw, Loader2, Eye, ChevronDown, ChevronUp,
  Building2, User, Mail, Tag, Calendar, MessageSquare, Shield, Search,
  EyeOff, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// ─── Types ────────────────────────────────────────────────────────────────────
type DocRow = {
  id: number;
  leadId: number;
  docType: string;
  docLabel: string | null;
  fileName: string;
  fileUrl: string;
  fileKey: string;
  mimeType: string | null;
  fileSize: number | null;
  status: "pending" | "approved" | "rejected" | "submitted";
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: Date | null;
  uploadedAt: Date;
  leadFirstName: string | null;
  leadLastName: string | null;
  leadEmail: string | null;
  leadBusinessName: string | null;
  leadSh360BusinessName: string | null;
  leadSh360OrgName: string | null;
  leadStage: string | null;
  leadVerticalName: string | null;
  leadRepCode: string | null;
};

type StatusFilter = "all" | "pending" | "approved" | "rejected" | "submitted";
const VERTICAL_OPTIONS = ["All Verticals", "Scale360", "Fundable360", "Share360", "Legacy360", "Media360"];

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  if (status === "submitted") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
      <CheckCircle className="w-3 h-3" /> Submitted
    </span>
  );
  if (status === "approved") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
      <CheckCircle className="w-3 h-3" /> Approved
    </span>
  );
  if (status === "rejected") return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5">
      <XCircle className="w-3 h-3" /> Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
      <Clock className="w-3 h-3" /> Pending Review
    </span>
  );
}

// ─── Review Modal ─────────────────────────────────────────────────────────────
function ReviewModal({
  doc,
  onClose,
  onSuccess,
}: {
  doc: DocRow;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [decision, setDecision] = useState<"approved" | "rejected" | null>(null);
  const [note, setNote] = useState(doc.reviewNote ?? "");
  const [reviewer, setReviewer] = useState("Admin");

  const reviewMutation = trpc.clientPortal.reviewDocument.useMutation({
    onSuccess: () => {
      toast.success(`Document ${decision === "approved" ? "approved" : "rejected"} successfully`);
      onSuccess();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = () => {
    if (!decision) { toast.error("Please select Approve or Reject"); return; }
    reviewMutation.mutate({
      docId: doc.id,
      status: decision,
      reviewNote: note.trim() || undefined,
      reviewedBy: reviewer.trim() || "Admin",
    });
  };

  const clientName = [doc.leadFirstName, doc.leadLastName].filter(Boolean).join(" ") || doc.leadSh360BusinessName || doc.leadSh360OrgName || doc.leadBusinessName || doc.leadEmail || "Unknown Client";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#84ab4a]" />
            Review Document
          </DialogTitle>
        </DialogHeader>

        {/* Document info */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#84ab4a]/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-[#84ab4a]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900">{doc.docLabel ?? doc.docType}</div>
              <div className="text-xs text-gray-500 truncate">{doc.fileName}</div>
              {doc.fileSize && (
                <div className="text-xs text-gray-400">{(doc.fileSize / 1024).toFixed(1)} KB</div>
              )}
            </div>
            <a
              href={`/api/files/${doc.fileKey}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-semibold text-[#84ab4a] hover:underline flex-shrink-0"
            >
              <Eye className="w-3.5 h-3.5" /> View File
            </a>
          </div>
          <div className="border-t border-gray-200 pt-2.5 grid grid-cols-2 gap-2 text-xs text-gray-500">
            <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {clientName}</div>
            {doc.leadEmail && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {doc.leadEmail}</div>}
            {(doc.leadSh360BusinessName || doc.leadSh360OrgName || doc.leadBusinessName) && (
              <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {doc.leadSh360BusinessName || doc.leadSh360OrgName || doc.leadBusinessName}</div>
            )}
            {doc.leadVerticalName && <div className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {doc.leadVerticalName}</div>}
            <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</div>
            {doc.leadRepCode && <div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Agent: {doc.leadRepCode}</div>}
          </div>
        </div>

        {/* Decision buttons */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Decision</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setDecision("approved")}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                decision === "approved"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 text-gray-500 hover:border-emerald-300 hover:bg-emerald-50/50"
              }`}
            >
              <CheckCircle className="w-4 h-4" /> Approve
            </button>
            <button
              onClick={() => setDecision("rejected")}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                decision === "rejected"
                  ? "border-red-500 bg-red-50 text-red-700"
                  : "border-gray-200 text-gray-500 hover:border-red-300 hover:bg-red-50/50"
              }`}
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
          </div>
        </div>

        {/* Note */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            <MessageSquare className="w-3.5 h-3.5 inline mr-1" />
            Review Note {decision === "rejected" && <span className="text-red-500">(required for rejections)</span>}
          </label>
          <textarea
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 resize-none"
            rows={3}
            placeholder={decision === "rejected" ? "Explain why the document was rejected..." : "Optional note for the client..."}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </div>

        {/* Reviewer name */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Reviewed By</label>
          <input
            type="text"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40"
            value={reviewer}
            onChange={e => setReviewer(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!decision || reviewMutation.isPending || (decision === "rejected" && !note.trim())}
            className={`flex-1 text-white border-0 ${
              decision === "approved" ? "bg-emerald-600 hover:bg-emerald-700" :
              decision === "rejected" ? "bg-red-600 hover:bg-red-700" :
              "bg-[#84ab4a] hover:bg-[#6e9038]"
            }`}
          >
            {reviewMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              decision === "approved" ? "Approve Document" :
              decision === "rejected" ? "Reject Document" :
              "Submit Review"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Lead Group Row ───────────────────────────────────────────────────────────
function LeadDocGroup({
  docs, onReview, onHide, onUnhide, isHidden = false,
}: {
  docs: DocRow[];
  onReview: (doc: DocRow) => void;
  onHide?: () => void;
  onUnhide?: () => void;
  isHidden?: boolean;
}) {
  const [expanded, setExpanded] = useState(!isHidden);
  const first = docs[0];

  const isOrphaned = !first.leadFirstName && !first.leadLastName && !first.leadEmail &&
    !first.leadBusinessName && !first.leadSh360BusinessName && !first.leadSh360OrgName;

  const clientName = isOrphaned
    ? `Deleted Client (Lead #${first.leadId})`
    : ([first.leadFirstName, first.leadLastName].filter(Boolean).join(" ") ||
       first.leadSh360BusinessName || first.leadSh360OrgName ||
       first.leadBusinessName || first.leadEmail || "Unknown Client");

  const pendingCount = docs.filter(d => d.status === "pending").length;
  const submittedCount = docs.filter(d => d.status === "submitted").length;
  const approvedCount = docs.filter(d => d.status === "approved").length;
  const rejectedCount = docs.filter(d => d.status === "rejected").length;

  const headerBg = isHidden
    ? "bg-gray-100/80 hover:bg-gray-200/60"
    : isOrphaned
      ? "bg-amber-50/80 hover:bg-amber-100/60"
      : "bg-gray-50/80 hover:bg-gray-100/80";

  return (
    <div className={`border rounded-2xl overflow-hidden ${isHidden ? "border-gray-200 opacity-70" : isOrphaned ? "border-amber-200" : "border-gray-100"}`}>
      {/* Lead header */}
      <div className={`flex items-center gap-3 px-5 py-3.5 transition-colors ${headerBg}`}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
            isOrphaned ? "bg-amber-100 text-amber-700" : "bg-[#84ab4a]/10 text-[#84ab4a]"
          }`}>
            {isOrphaned
              ? <AlertCircle className="w-4 h-4" />
              : <>{(first.leadFirstName?.[0] ?? "?")}{(first.leadLastName?.[0] ?? "")}</>
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold text-sm flex items-center gap-1.5 flex-wrap ${isOrphaned ? "text-amber-800" : "text-gray-900"}`}>
              {clientName}
              {isOrphaned && (
                <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Deleted</span>
              )}
              {isHidden && (
                <span className="text-[10px] font-semibold bg-gray-200 text-gray-500 border border-gray-300 rounded-full px-2 py-0.5">Hidden</span>
              )}
            </div>
            <div className="text-xs text-gray-400 truncate">
              {first.leadBusinessName && `${first.leadBusinessName} · `}
              {first.leadVerticalName && `${first.leadVerticalName} · `}
              {first.leadRepCode && `Agent: ${first.leadRepCode}`}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {pendingCount > 0 && (
              <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{pendingCount} pending</span>
            )}
            {submittedCount > 0 && (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{submittedCount} submitted</span>
            )}
            {approvedCount > 0 && (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{approvedCount} approved</span>
            )}
            {rejectedCount > 0 && (
              <span className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">{rejectedCount} rejected</span>
            )}
            <span className="text-xs text-gray-400">{docs.length} doc{docs.length !== 1 ? "s" : ""}</span>
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </button>

        {/* Hide / Unhide button */}
        {isHidden ? (
          <button
            onClick={onUnhide}
            title="Unhide this group"
            className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-gray-500 border border-gray-300 bg-white rounded-lg px-2.5 py-1 hover:bg-gray-50 transition-colors ml-1"
          >
            <Eye className="w-3.5 h-3.5" /> Unhide
          </button>
        ) : (
          <button
            onClick={onHide}
            title="Hide this group from the main list"
            className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-gray-400 border border-gray-200 bg-white rounded-lg px-2.5 py-1 hover:bg-gray-50 hover:text-gray-600 transition-colors ml-1"
          >
            <EyeOff className="w-3.5 h-3.5" /> Hide
          </button>
        )}
      </div>

      {/* Documents */}
      {expanded && (
        <div className="divide-y divide-gray-50">
          {docs.map(doc => (
            <div key={doc.id} className="px-5 py-3.5 flex items-center gap-3 bg-white">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-800">{doc.docLabel ?? doc.docType}</div>
                <div className="text-xs text-gray-400 truncate">{doc.fileName}</div>
                {doc.status === "rejected" && doc.reviewNote && (
                  <div className="mt-1 flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide">Rejected Reason: </span>
                      <span className="text-xs text-red-700 whitespace-pre-line">{doc.reviewNote}</span>
                    </div>
                  </div>
                )}
                {doc.status !== "rejected" && doc.reviewNote && (
                  <div className="text-xs text-gray-500 mt-0.5 italic">Note: {doc.reviewNote}</div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="text-xs text-gray-400">{new Date(doc.uploadedAt).toLocaleDateString()}</div>
                <StatusBadge status={doc.status} />
                <a
                  href={`/api/files/${doc.fileKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="View file"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={() => onReview(doc)}
                  className="text-xs font-semibold text-[#84ab4a] border border-[#84ab4a]/30 bg-[#84ab4a]/5 rounded-lg px-2.5 py-1 hover:bg-[#84ab4a]/10 transition-colors"
                >
                  Review
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ClientDocumentReview() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [verticalFilter, setVerticalFilter] = useState("All Verticals");
  const [searchQuery, setSearchQuery] = useState("");
  const [reviewingDoc, setReviewingDoc] = useState<DocRow | null>(null);
  const [showHidden, setShowHidden] = useState(false);

  const { data: docs = [], isLoading, refetch } = trpc.clientPortal.adminListDocuments.useQuery(
    { status: statusFilter === "all" ? "all" : statusFilter },
    { refetchInterval: 30_000 }
  );

  const { data: hiddenIds = [], refetch: refetchHidden } = trpc.clientPortal.getHiddenLeadDocGroups.useQuery();

  const hideMutation = trpc.clientPortal.hideLeadDocGroup.useMutation({
    onSuccess: () => { refetchHidden(); toast.success("Group hidden from main view."); },
    onError: (e) => toast.error(e.message),
  });

  const unhideMutation = trpc.clientPortal.unhideLeadDocGroup.useMutation({
    onSuccess: () => { refetchHidden(); toast.success("Group restored to main view."); },
    onError: (e) => toast.error(e.message),
  });

  // Stats (based on raw docs, before search/vertical filter)
  const pendingCount = docs.filter(d => d.status === "pending").length;
  const submittedCount = docs.filter(d => d.status === "submitted").length;
  const approvedCount = docs.filter(d => d.status === "approved").length;
  const rejectedCount = docs.filter(d => d.status === "rejected").length;

  // Apply search + vertical filter
  const filteredDocs = docs.filter(d => {
    if (verticalFilter !== "All Verticals" && d.leadVerticalName !== verticalFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = `${d.leadFirstName ?? ""} ${d.leadLastName ?? ""}`.toLowerCase();
      const biz = (d.leadBusinessName ?? "").toLowerCase();
      const email = (d.leadEmail ?? "").toLowerCase();
      const rep = (d.leadRepCode ?? "").toLowerCase();
      if (!name.includes(q) && !biz.includes(q) && !email.includes(q) && !rep.includes(q)) return false;
    }
    return true;
  });

  const hiddenSet = new Set(hiddenIds as number[]);

  // Separate visible and hidden groups
  const visibleDocs = filteredDocs.filter(d => !hiddenSet.has(d.leadId));
  const hiddenDocs = filteredDocs.filter(d => hiddenSet.has(d.leadId));

  // Group by leadId
  const grouped = visibleDocs.reduce<Record<number, DocRow[]>>((acc, doc) => {
    if (!acc[doc.leadId]) acc[doc.leadId] = [];
    acc[doc.leadId].push(doc as DocRow);
    return acc;
  }, {});

  const groupedHidden = hiddenDocs.reduce<Record<number, DocRow[]>>((acc, doc) => {
    if (!acc[doc.leadId]) acc[doc.leadId] = [];
    acc[doc.leadId].push(doc as DocRow);
    return acc;
  }, {});

  const FILTERS: { key: StatusFilter; label: string; color: string }[] = [
    { key: "all", label: "All Documents", color: "#6B7280" },
    { key: "submitted", label: "Submitted", color: "#10B981" },
    { key: "pending", label: "Pending Review", color: "#F59E0B" },
    { key: "approved", label: "Approved", color: "#10B981" },
    { key: "rejected", label: "Rejected", color: "#EF4444" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Client Document Review</h2>
          <p className="text-sm text-gray-400">Review and approve documents uploaded by clients through their portal.</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        {[
          { label: "Total Docs", count: docs.length, color: "#6B7280", icon: FileText },
          { label: "Submitted", count: submittedCount, color: "#10B981", icon: CheckCircle },
          { label: "Pending", count: pendingCount, color: "#F59E0B", icon: Clock },
          { label: "Approved", count: approvedCount, color: "#10B981", icon: CheckCircle },
          { label: "Rejected", count: rejectedCount, color: "#EF4444", icon: XCircle },
        ].map(({ label, count, color, icon: Icon }) => (
          <div key={label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
            <div className="text-2xl font-bold" style={{ color }}>{count}</div>
            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">{label}</div>
          </div>
        ))}
      </div>

      {/* Search + Filters row */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client name, email, business, or rep code…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 bg-white"
          />
        </div>
        <select
          value={verticalFilter}
          onChange={e => setVerticalFilter(e.target.value)}
          className="text-xs font-semibold px-3 py-2 border border-gray-200 rounded-xl bg-white text-gray-600 focus:outline-none focus:border-[#84ab4a]/60"
        >
          {VERTICAL_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {/* Status filter tabs */}
      <div className="flex items-center gap-2 mb-5">
        <Filter className="w-4 h-4 text-gray-400" />
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
              statusFilter === f.key
                ? "text-white border-transparent"
                : "text-gray-500 border-gray-200 hover:border-gray-300 bg-white"
            }`}
            style={statusFilter === f.key ? { background: f.color, borderColor: f.color } : {}}
          >
            {f.label}
            {f.key === "pending" && pendingCount > 0 && (
              <span className="ml-1.5 bg-white/30 rounded-full px-1.5 text-[10px]">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Document groups */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#84ab4a] animate-spin" />
        </div>
      ) : Object.keys(grouped).length === 0 && Object.keys(groupedHidden).length === 0 ? (
        <div className="py-16 text-center text-gray-400 bg-white rounded-2xl border border-gray-100">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No documents found</p>
          <p className="text-sm mt-1">
            {statusFilter === "submitted" ? "No submitted documents found." :
             statusFilter === "pending" ? "No documents are awaiting review." :
             statusFilter === "approved" ? "No documents have been approved yet." :
             statusFilter === "rejected" ? "No documents have been rejected." :
             "Clients haven't uploaded any documents yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([leadId, leadDocs]) => (
            <LeadDocGroup
              key={leadId}
              docs={leadDocs as DocRow[]}
              onReview={setReviewingDoc}
              onHide={() => hideMutation.mutate({ leadId: parseInt(leadId) })}
            />
          ))}

          {/* Hidden groups section */}
          {(hiddenIds as number[]).length > 0 && (
            <div className="pt-2">
              <button
                onClick={() => setShowHidden(v => !v)}
                className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors mb-3"
              >
                <EyeOff className="w-3.5 h-3.5" />
                {showHidden ? "Hide" : "Show"} hidden groups ({(hiddenIds as number[]).length})
                {showHidden ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>

              {showHidden && Object.keys(groupedHidden).length > 0 && (
                <div className="space-y-3">
                  {Object.entries(groupedHidden).map(([leadId, leadDocs]) => (
                    <LeadDocGroup
                      key={leadId}
                      docs={leadDocs as DocRow[]}
                      onReview={setReviewingDoc}
                      isHidden
                      onUnhide={() => unhideMutation.mutate({ leadId: parseInt(leadId) })}
                    />
                  ))}
                </div>
              )}

              {showHidden && Object.keys(groupedHidden).length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">
                  Hidden groups don't match the current filter — clear filters to see them.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Review modal */}
      {reviewingDoc && (
        <ReviewModal
          doc={reviewingDoc}
          onClose={() => setReviewingDoc(null)}
          onSuccess={() => { refetch(); setReviewingDoc(null); }}
        />
      )}
    </div>
  );
}
